import { useMemo } from 'react';
import {
  buildSuiteCounts,
  buildTestCounts,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import {
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  type MetricCardItem,
  type MetricCardOption,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import {
  suiteExecutionSummaryStatusOptions,
  testExecutionSummaryStatusOptions,
} from '@/shared/constants/statusOptions';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Text } from '@/ui/primitives';
import type { ResultsFileDto, SuiteExecutionStatus, TestExecutionStatus } from '@flowqa/shared';

export type ResultsFileStatusRowsProps = {
  resultsFile: ResultsFileDto;
};

const sumCounts = (counts: Partial<Record<string, number>>): number =>
  Object.values(counts).reduce<number>((total, value) => total + (value ?? 0), 0);

export const ResultsFileStatusRows = ({ resultsFile }: ResultsFileStatusRowsProps): JSX.Element => {
  const suiteCounts = useMemo(
    () =>
      buildSuiteCounts({
        passed: resultsFile.suiteExecutionPassed ?? 0,
        failed: resultsFile.suiteExecutionFailed ?? 0,
        errored: resultsFile.suiteExecutionErrored ?? 0,
        skipped: resultsFile.suiteExecutionSkipped ?? 0,
        untested: resultsFile.suiteExecutionUntested ?? 0,
      }),
    [
      resultsFile.suiteExecutionErrored,
      resultsFile.suiteExecutionFailed,
      resultsFile.suiteExecutionPassed,
      resultsFile.suiteExecutionSkipped,
      resultsFile.suiteExecutionUntested,
    ],
  );
  const testCounts = useMemo(
    () =>
      buildTestCounts({
        passed: resultsFile.testExecutionPassed ?? 0,
        failed: resultsFile.testExecutionFailed ?? 0,
        errored: resultsFile.testExecutionErrored ?? 0,
        skipped: resultsFile.testExecutionSkipped ?? 0,
        untested: resultsFile.testExecutionUntested ?? 0,
      }),
    [
      resultsFile.testExecutionErrored,
      resultsFile.testExecutionFailed,
      resultsFile.testExecutionPassed,
      resultsFile.testExecutionSkipped,
      resultsFile.testExecutionUntested,
    ],
  );
  const suiteOptions = useMemo<Array<MetricCardOption<SuiteExecutionStatus>>>(
    () => mapStatusOptionsToMetricOptions(suiteExecutionSummaryStatusOptions, resolveMetricTone),
    [],
  );
  const testOptions = useMemo<Array<MetricCardOption<TestExecutionStatus>>>(
    () => mapStatusOptionsToMetricOptions(testExecutionSummaryStatusOptions, resolveMetricTone),
    [],
  );
  const suiteCards = useMemo<MetricCardItem[]>(
    () =>
      buildStatusMetricCards({
        options: suiteOptions,
        counts: suiteCounts,
        total: sumCounts(suiteCounts),
        loading: false,
      }),
    [suiteCounts, suiteOptions],
  );
  const testCards = useMemo<MetricCardItem[]>(
    () =>
      buildStatusMetricCards({
        options: testOptions,
        counts: testCounts,
        total: sumCounts(testCounts),
        loading: false,
      }),
    [testCounts, testOptions],
  );

  return (
    <Section paddingY="md">
      <Stack gap="lg">
        <Stack gap="none">
          <Text role="label" color="textSecondary">
            Suites
          </Text>
          <StatusMetricCardsRow cards={suiteCards} />
        </Stack>
        <Stack gap="none">
          <Text role="label" color="textSecondary">
            Tests
          </Text>
          <StatusMetricCardsRow cards={testCards} />
        </Stack>
      </Stack>
    </Section>
  );
};
